import { getToken } from "@/lib/supabase";
import type {
  CreateTokenResponse,
  ValidateTokenResponse,
  SubmitReviewParams,
  UploadReviewImageParams,
  UploadReviewImageResponse,
  Review,
  ReviewStatus,
} from "@/types/review";

const API_BASE = "/api";

export async function createReviewToken(artistId: string): Promise<CreateTokenResponse> {
  const token = getToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE}/review-tokens`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ artistId }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to create token");

  return data;
}

export async function validateReviewToken(
  artistSlug: string,
  token: string
): Promise<ValidateTokenResponse> {
  const response = await fetch(
    `${API_BASE}/review-tokens/validate?slug=${encodeURIComponent(artistSlug)}&token=${encodeURIComponent(token)}`
  );

  const data = await response.json();
  if (!response.ok) {
    return { valid: false, error: data.error || "Invalid token" };
  }

  return data;
}

export async function submitReview(params: SubmitReviewParams): Promise<void> {
  const response = await fetch(`${API_BASE}/reviews`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to submit review");
}

export async function uploadReviewImage(
  params: UploadReviewImageParams
): Promise<UploadReviewImageResponse> {
  const formData = new FormData();
  formData.append("file", params.file);
  formData.append("token", params.token);
  formData.append("cropX", String(params.cropX));
  formData.append("cropY", String(params.cropY));
  formData.append("cropWidth", String(params.cropWidth));
  formData.append("cropHeight", String(params.cropHeight));

  const response = await fetch(`${API_BASE}/review-image`, {
    method: "POST",
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to upload image");

  return data;
}

export async function getReviews(params: {
  artistId?: string;
  status?: ReviewStatus;
  limit?: number;
} = {}): Promise<Review[]> {
  const searchParams = new URLSearchParams();
  if (params.artistId) searchParams.set("artistId", params.artistId);
  if (params.status) searchParams.set("status", params.status);
  if (params.limit) searchParams.set("limit", String(params.limit));

  const token = getToken();
  const headers: HeadersInit = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(
    `${API_BASE}/reviews${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
    { headers }
  );

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to fetch reviews");

  return data.reviews;
}

export async function moderateReview(
  reviewId: string,
  action: "approve" | "reject",
  updates?: { reviewText?: string; adminNotes?: string }
): Promise<void> {
  const token = getToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${API_BASE}/reviews/${reviewId}/moderate`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action, ...updates }),
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to moderate review");
}
